import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Check, ChevronsUpDown, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * A searchable, keyboard-friendly Popover dropdown.
 * Generic so it works for leads, projects, customers, or any list of objects.
 *
 * @param items         Array of objects to pick from (local mode).
 * @param value         Currently selected id (matches item.id).
 * @param onChange      Called with the picked item.id when the user selects a row.
 * @param renderRow     (item) => JSX rendered inside each row.
 * @param matchItem     (item, needle) => boolean. Lowercased needle is passed.
 * @param onSearch      OPTIONAL legacy async mode: (needle) => void — parent owns
 *                      filtering; client-side matchItem filtering is skipped.
 * @param optionsEndpoint OPTIONAL remote mode, e.g. "/leads/options": the picker
 *                      owns fetching. On first open it loads limit=21 — if the
 *                      dataset has ≤20 rows it shows them all (client filter);
 *                      if larger, it shows nothing until the user types 3+
 *                      characters, then queries `?q=<needle>&limit=20`. The
 *                      current selection is always kept via `include_id`.
 * @param onSelectItem  OPTIONAL (item|null) — fires on pick, on clear, and once
 *                      when the initial load resolves the current value. Use it
 *                      for form autofill instead of searching a local items list.
 * @param placeholder   Placeholder text on the trigger when nothing is selected.
 * @param selectedLabel (item) => string shown on the trigger when an item is picked.
 * @param disabled      Disable trigger.
 * @param testid        data-testid prefix.
 * @param emptyHint     What to show when no matches.
 * @param renderLimit   Max rows rendered (defaults 50). Keeps render cheap for huge lists.
 */
export default function SearchablePicker({
  items = [],
  value,
  onChange,
  renderRow,
  matchItem,
  onSearch,
  optionsEndpoint,
  onSelectItem,
  placeholder = "Search…",
  selectedLabel,
  disabled = false,
  testid = "searchable-picker",
  emptyHint = "No matches",
  renderLimit = 50,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // Remote mode (optionsEndpoint): null = not loaded yet.
  const [remote, setRemote] = useState(null);
  const [asyncMode, setAsyncMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const resolvedFor = useRef(null);

  const source = optionsEndpoint ? (remote || []) : items;
  const selected = source.find((it) => it.id === value) || null;
  const needle = q.trim().toLowerCase();

  // Remote: initial sentinel load on mount — 21 rows means the dataset
  // exceeds 20, so switch to 3-letter server search instead of shipping it.
  // (Mount rather than first-open so pre-selected editors resolve their
  // label/autofill via include_id.)
  useEffect(() => {
    if (!optionsEndpoint || remote !== null) return;
    setLoading(true);
    api.get(`${optionsEndpoint}?limit=21${value ? `&include_id=${value}` : ""}`)
      .then((r) => {
        const rows = r.data || [];
        if (rows.length > 20) {
          setAsyncMode(true);
          setRemote(rows.filter((x) => x.id === value));
        } else {
          setAsyncMode(false);
          setRemote(rows.slice(0, 20));
        }
      })
      .catch(() => setRemote([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, optionsEndpoint]);

  // Remote: debounced server search — async mode only, 3+ characters.
  useEffect(() => {
    if (!optionsEndpoint || !asyncMode) return;
    const needleRaw = q.trim();
    if (needleRaw.length < 3) { setRemote((prev) => prev.filter((x) => x.id === value)); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`${optionsEndpoint}?limit=20&q=${encodeURIComponent(needleRaw)}${value ? `&include_id=${value}` : ""}`)
        .then((r) => {
          const rows = r.data || [];
          setRemote((prev) => {
            const sel = prev.find((x) => x.id === value);
            return sel && !rows.some((x) => x.id === sel.id) ? [sel, ...rows] : rows;
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, asyncMode, optionsEndpoint]);

  // Legacy async mode: debounce keystrokes up to the parent's search handler.
  useEffect(() => {
    if (!onSearch || optionsEndpoint) return;
    const t = setTimeout(() => onSearch(q.trim()), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Let parents autofill from the resolved selected object (initial load or pick).
  // Second arg: initial=true when fired by the initial resolve, false on user pick.
  useEffect(() => {
    if (!onSelectItem) return;
    if (!value) { resolvedFor.current = null; return; }
    if (selected && resolvedFor.current !== value) {
      resolvedFor.current = value;
      onSelectItem(selected, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, value]);

  const filtered = optionsEndpoint || onSearch
    ? source
    : (!needle ? source : source.filter((it) => matchItem(it, needle)));
  const capped = filtered.slice(0, renderLimit);

  const pick = (it) => { onSelectItem?.(it); onChange(it.id); setOpen(false); };
  const clear = (e) => { e.stopPropagation(); onSelectItem?.(null); onChange(""); };

  // Remote async mode with too-short needle: prompt instead of listing.
  const needsMoreChars = optionsEndpoint && asyncMode && q.trim().length < 3;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="w-full mt-2 h-10 px-3 border border-[#E5E7EB] bg-white text-left text-sm flex items-center justify-between hover:border-[#0a0a0a] disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
          data-testid={`${testid}-trigger`}
        >
          <span className={selected ? "" : "text-[#9CA3AF]"}>
            {selected ? selectedLabel(selected) : (optionsEndpoint && value && loading ? "Loading…" : placeholder)}
          </span>
          <div className="flex items-center gap-1">
            {selected && !disabled && (
              <span
                role="button"
                tabIndex={0}
                onClick={clear}
                className="text-[#9CA3AF] hover:text-[#DC2626]"
                data-testid={`${testid}-clear`}
                aria-label="Clear selection"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronsUpDown className="w-3.5 h-3.5 text-[#9CA3AF]" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] rounded-none border-[#0a0a0a]" align="start">
        <div className="flex items-center border-b border-[#E5E7EB] px-3 h-10">
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-[#9CA3AF]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent outline-none text-sm py-2 placeholder:text-[#9CA3AF]"
            data-testid={`${testid}-search`}
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {loading && (
            <div className="text-center text-xs text-[#9CA3AF] py-6" data-testid={`${testid}-loading`}>Searching…</div>
          )}
          {!loading && needsMoreChars && (
            <div className="text-center text-xs text-[#9CA3AF] py-6" data-testid={`${testid}-type-more`}>
              Type at least 3 characters to search
            </div>
          )}
          {!loading && !needsMoreChars && capped.length === 0 && (
            <div className="text-center text-xs text-[#9CA3AF] py-6" data-testid={`${testid}-empty`}>
              {emptyHint}
            </div>
          )}
          {!needsMoreChars && capped.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => pick(it)}
              className={`w-full text-left px-3 py-2 hover:bg-[#F9FAFB] flex items-start justify-between gap-2 ${value === it.id ? "bg-[#EFF6FF]" : ""}`}
              data-testid={`${testid}-option-${it.id}`}
            >
              <div className="min-w-0 flex-1">{renderRow(it)}</div>
              {value === it.id && <Check className="w-3.5 h-3.5 text-[#002FA7] shrink-0 mt-1" />}
            </button>
          ))}
          {!optionsEndpoint && filtered.length > renderLimit && (
            <div className="text-center text-[10px] text-[#9CA3AF] py-2 uppercase tracking-widest">
              Showing first {renderLimit} of {filtered.length} — keep typing to narrow down
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Convenience pre-built matchers + row renderers — keep callers DRY ----------

export function leadMatch(l, needle) {
  return (
    (l.name || "").toLowerCase().includes(needle) ||
    (l.phone || "").toLowerCase().includes(needle) ||
    (l.email || "").toLowerCase().includes(needle) ||
    (l.district || "").toLowerCase().includes(needle) ||
    (l.outside_kerala_city || "").toLowerCase().includes(needle)
  );
}

export function renderLeadRow(l) {
  return (
    <>
      <div className="text-sm font-semibold truncate">{l.name || "(no name)"}</div>
      <div className="text-[11px] text-[#4B5563] truncate">
        {l.phone || "no phone"} · {l.district || l.outside_kerala_city || "—"}
        {l.capacity_kw ? ` · ${l.capacity_kw} kW` : ""}
      </div>
    </>
  );
}

export function projectMatch(p, needle) {
  return (
    (p.customer_name || "").toLowerCase().includes(needle) ||
    (p.qr_code || "").toLowerCase().includes(needle) ||
    (p.location || "").toLowerCase().includes(needle) ||
    (p.project_manager || "").toLowerCase().includes(needle)
  );
}

export function renderProjectRow(p) {
  return (
    <>
      <div className="text-sm font-semibold truncate">{p.customer_name || "(no name)"}</div>
      <div className="text-[11px] text-[#4B5563] truncate">
        {p.qr_code} · {p.location || "—"}
        {p.capacity_kw ? ` · ${p.capacity_kw} kW` : ""}
      </div>
    </>
  );
}

export function productMatch(p, needle) {
  return (
    (p.name || "").toLowerCase().includes(needle) ||
    (p.category || "").toLowerCase().includes(needle) ||
    (p.unit_of_measure || "").toLowerCase().includes(needle)
  );
}

export function renderProductRow(p) {
  return (
    <>
      <div className="text-sm font-semibold truncate">{p.name || "(no name)"}</div>
      <div className="text-[11px] text-[#4B5563] truncate">
        {p.category || "—"}
        {p.unit_of_measure ? ` · ${p.unit_of_measure}` : ""}
      </div>
    </>
  );
}
